import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { UserPlusIcon, TrashIcon } from '@heroicons/react/24/solid';
import { Link } from 'react-router-dom';

// Reusable form components
const FormInput = ({ id, label, type, value, onChange, required = true }) => (
    <div>
        <label htmlFor={id} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{label}</label>
        <input type={type} id={id} name={id} value={value} onChange={onChange} required={required} className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white" />
    </div>
);
const SelectInput = ({ id, label, value, onChange, options, required = true }) => (
    <div>
        <label htmlFor={id} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{label}</label>
        <select id={id} name={id} value={value} onChange={onChange} required={required} className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white">
            <option value="">Select a role...</option>
            {options.map(opt => <option key={opt.id} value={opt.id}>{opt.name}</option>)}
        </select>
    </div>
);

export default function ManageUsersPage() {
    const { user: currentUser, token } = useAuth();
    const [users, setUsers] = useState([]);
    const [roles, setRoles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');
    const [formData, setFormData] = useState({ firstName: '', lastName: '', email: '', password: '', roleId: '' });
    const [filter, setFilter] = useState('all');

    const fetchData = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
        console.log("Fetching /admin/users/all...");
        const usersRes = await fetch(`${process.env.REACT_APP_API_URL}/admin/users/all`, { headers: { 'Authorization': `Bearer ${token}` }});
        console.log("Users Response Status:", usersRes.status);

        console.log("Fetching /admin/users/roles...");
        const rolesRes = await fetch(`${process.env.REACT_APP_API_URL}/admin/users/roles`, { headers: { 'Authorization': `Bearer ${token}` }});
        console.log("Roles Response Status:", rolesRes.status);
        
        if (!usersRes.ok || !rolesRes.ok) throw new Error('Could not fetch data.');
            const usersData = await usersRes.json();
            const rolesData = await rolesRes.json();
            setUsers(usersData);
            setRoles(rolesData);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const handleChange = (e) => setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        setMessage('');
        setError('');
        setIsSubmitting(true);
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/users`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify(formData)
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.message);
            
            setMessage(`Successfully created admin: ${data.first_name}`);
            setFormData({ firstName: '', lastName: '', email: '', password: '', roleId: '' });
            fetchData();
        } catch (err) {
            setError(err.message);
        } finally {
            setIsSubmitting(false);
        }
    };
    
    const handleDelete = async (userIdToDelete) => {
        if (userIdToDelete === currentUser.id) {
            alert("You cannot delete your own account.");
            return;
        }
        if (!window.confirm('Are you sure you want to permanently delete this user and all their data? This cannot be undone.')) return;
        
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/users/${userIdToDelete}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.message);
            
            setMessage(data.message);
            fetchData();
        } catch (err) {
            setError(err.message);
        }
    };

    const filteredUsers = users.filter(u => {
        if (filter === 'all') return true;
        if (filter === 'admin') {
            const role = u.role ? u.role.toLowerCase() : '';
            return role !== 'recipient' && role !== 'donor' && role !== 'n/a';
        }
        return u.role === filter;
    });

    if (loading) return <p className="p-8 text-center">Loading users...</p>;

    return (
        <div className="space-y-8">
            <h1 className="text-4xl font-extrabold text-gray-900 dark:text-white">Manage Users</h1>
            {error && <p className="p-4 text-center font-semibold text-red-500 bg-red-100 dark:bg-red-900/50 rounded-lg">{error}</p>}
            {message && <p className="p-4 text-center font-semibold text-green-500 bg-green-100 dark:bg-green-900/50 rounded-lg">{message}</p>}
            
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
                <div className="lg:col-span-1">
                    <div className="bg-white dark:bg-gray-800 p-8 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700">
                        <form onSubmit={handleSubmit} className="space-y-6">
                            <div className="flex items-center gap-3">
                                <UserPlusIcon className="w-8 h-8 text-orange-500" />
                                <h2 className="text-2xl font-bold text-gray-800 dark:text-white">Create New Admin</h2>
                            </div>
                            <FormInput id="firstName" name="firstName" label="First Name" type="text" value={formData.firstName} onChange={handleChange} />
                            <FormInput id="lastName" name="lastName" label="Last Name (Optional)" type="text" value={formData.lastName} onChange={handleChange} required={false} />
                            <FormInput id="email" name="email" label="Email" type="email" value={formData.email} onChange={handleChange} />
                            <FormInput id="password" name="password" label="Temporary Password" type="password" value={formData.password} onChange={handleChange} />
                            <SelectInput id="roleId" name="roleId" label="Assign Role" value={formData.roleId} onChange={handleChange} options={roles} />
                            
                            <button type="submit" disabled={isSubmitting} className="w-full py-3 bg-orange-500 text-white font-bold rounded-lg hover:bg-orange-600 transition-colors disabled:opacity-50">
                                {isSubmitting ? 'Creating...' : 'Create Admin'}
                            </button>
                        </form>
                    </div>
                </div>

                <div className="lg:col-span-2">
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700">
                        <div className="flex flex-col sm:flex-row justify-between items-center mb-4">
                            <h2 className="text-2xl font-bold text-gray-800 dark:text-white">Existing Users ({filteredUsers.length})</h2>
                            <div className="flex gap-2 mt-4 sm:mt-0">
                                <button onClick={() => setFilter('all')} className={`px-3 py-1 text-sm rounded-full ${filter === 'all' ? 'bg-orange-500 text-white' : 'bg-gray-200 dark:bg-gray-700'}`}>All</button>
                                <button onClick={() => setFilter('admin')} className={`px-3 py-1 text-sm rounded-full ${filter === 'admin' ? 'bg-orange-500 text-white' : 'bg-gray-200 dark:bg-gray-700'}`}>Admins</button>
                                <button onClick={() => setFilter('recipient')} className={`px-3 py-1 text-sm rounded-full ${filter === 'recipient' ? 'bg-orange-500 text-white' : 'bg-gray-200 dark:bg-gray-700'}`}>Recipients</button>
                                <button onClick={() => setFilter('donor')} className={`px-3 py-1 text-sm rounded-full ${filter === 'donor' ? 'bg-orange-500 text-white' : 'bg-gray-200 dark:bg-gray-700'}`}>Donors</button>
                            </div>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full text-left">
                                <thead className="bg-gray-50 dark:bg-gray-700">
                                    <tr>
                                        <th className="p-3 text-sm font-semibold text-gray-600 dark:text-gray-300">Name</th>
                                        <th className="p-3 text-sm font-semibold text-gray-600 dark:text-gray-300">Email</th>
                                        <th className="p-3 text-sm font-semibold text-gray-600 dark:text-gray-300">Role</th>
                                        <th className="p-3 text-sm font-semibold text-gray-600 dark:text-gray-300 text-right">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredUsers.length > 0 ? filteredUsers.map(u => (
                                        <tr key={u.id} className="border-b dark:border-gray-600 last:border-b-0">
                                            <td className="p-3">
                                                <Link to={`/admin/users/${u.id}/history`} className="hover:underline text-orange-500">
                                                    {u.first_name} {u.last_name}
                                                </Link>
                                            </td>
                                            <td className="p-3">{u.email}</td>
                                            <td className="p-3">
                                                <span className={`text-xs font-semibold capitalize px-2.5 py-0.5 rounded-full ${u.role.toLowerCase().includes('admin') ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300' : 'bg-gray-100 text-gray-800 dark:bg-gray-600 dark:text-gray-300'}`}>
                                                    {u.role.replace('_', ' ')}
                                                </span>
                                            </td>
                                            <td className="p-3 text-right">
                                                <button 
                                                    onClick={() => handleDelete(u.id)}
                                                    disabled={u.id === currentUser.id}
                                                    className="p-1 text-red-500 hover:text-red-700 disabled:text-gray-400 disabled:cursor-not-allowed"
                                                    title={u.id === currentUser.id ? "Cannot delete yourself" : "Delete User"}
                                                >
                                                    <TrashIcon className="w-5 h-5" />
                                                </button>
                                            </td>
                                        </tr>
                                    )) : (
                                        <tr>
                                            <td colSpan="4" className="text-center p-8 text-gray-500">No users match the current filter.</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}